import { useAtom, useAtomValue } from "jotai"
import { colorPickerSessionAtom } from "@/store/color-picker-state"
import {
	getColorPipetteCursor,
	unprojectPointerCoord,
} from "../canvas-controls/canvas-controls-utils"
import { usePointerDrag } from "@/utils/usePointerDrag"
import { useEditor } from "@/utils/useEditor"
import { PixelGridColorPickerPreview, RoundedColorPickerPreview } from "./ColorPickerPreview"
import { useEffect, useState } from "react"
import { mat4, vec2 } from "gl-matrix"
import { ColorSampleResult } from "@/core/color-sampler"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import { useShortcuts } from "@/utils/useShortcuts"
import { actionShortcuts } from "@/shortcuts/shortcuts"

export const ColorPickerOverlay = () => {
	const editor = useEditor()
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const [colorPickerSession, setColorPickerSession] = useAtom(colorPickerSessionAtom)
	const [previewPosition, setPreviewPosition] = useState<vec2 | null>(null)
	const [samplingResult, setSamplingResult] = useState<ColorSampleResult | null>(null)

	useShortcuts({
		keys: [actionShortcuts.escape],
		action: () => {
			setColorPickerSession(null)
			setSamplingResult(null)
		},
		trackingLabel: "cancel_color_picker",
		disabled: colorPickerSession === null,
	})

	useEffect(() => {
		if (!colorPickerSession) {
			return
		}

		setSamplingResult(null)
		editor.stopSamplingColor()
	}, [currentPhoto])

	useEffect(() => {
		window.addEventListener("pointerdown", handleOutsideClick, { passive: true })

		return () => {
			window.removeEventListener("pointerdown", handleOutsideClick)
		}
	}, [])

	useEffect(() => {
		setPreviewPosition(null)
	}, [colorPickerSession])

	const bindDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			e.preventDefault()
			e.stopPropagation()

			// Cancelled by user
			if (e.button !== 0) {
				setSamplingResult(null)
				setColorPickerSession(null)
				return
			}

			// Out of boundaries
			if (previewPosition === null) {
				setSamplingResult(null)
				setColorPickerSession(null)
				return
			}

			// Not cancelled, calling the handler
			if (samplingResult) {
				const isFinished = colorPickerSession?.handler(samplingResult)

				if (isFinished) {
					setSamplingResult(null)
				}
			}
		},
		onPointerMove(e) {
			if (!colorPickerSession) {
				return
			}

			e.preventDefault()
			e.stopPropagation()

			e.currentTarget.style.cursor = getColorPipetteCursor()

			const viewportMatrixInv = mat4.create()
			mat4.invert(viewportMatrixInv, editor.mainViewport.viewMatrix)

			const coords = unprojectPointerCoord(e.pageX, e.pageY, viewportMatrixInv)

			if (coords[0] > 1 || coords[1] > 1 || coords[0] < 0 || coords[1] < 0) {
				setPreviewPosition(null)
				return
			}

			setPreviewPosition([e.clientX + 24, e.clientY])

			editor.sampleColor(coords).then((result) => {
				setSamplingResult(result)
			})
		},
		onDrag() {
			// Nothing to do
		},
	})

	const handleOutsideClick = () => {
		if (!previewPosition) {
			setColorPickerSession(null)
			setSamplingResult(null)
		}
	}

	if (colorPickerSession === null) {
		return null
	}

	const renderPreview = () => {
		if (!previewPosition || !samplingResult) {
			return null
		}

		if (samplingResult.originalPurpose === "white-balance") {
			return (
				<PixelGridColorPickerPreview
					result={samplingResult}
					x={previewPosition[0]}
					y={previewPosition[1]}
				/>
			)
		} else if (samplingResult.originalPurpose === "point-color") {
			return (
				<RoundedColorPickerPreview
					result={samplingResult}
					x={previewPosition[0]}
					y={previewPosition[1]}
				/>
			)
		}
	}

	return (
		<div data-colorpicker="true" className="absolute inset-0" {...bindDrag}>
			{renderPreview()}
		</div>
	)
}
